import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from '../lib/routing';
import Header from '../components/header';
import Markdown from '../components/markdown';
import ToolChip from '../components/tool-chip';
import WidgetFrame from '../components/widget-frame';
import TypingDots from '../components/typing-dots';
import { cn } from '@/lib/utils';
import {
  adminToolLabel,
  listAdminMessages,
  streamAdminChat,
  type AdminMsg,
  type AdminToolCall,
  type AdminWidget,
} from '../lib/admin-chat';
import { clearTurn, startTurn, useLiveTurn } from '../lib/live-turn';
import { useWebVoice } from '../lib/use-web-voice';
import { VoiceMicButton, VoiceStatusBar } from '../components/voice-controls';
import { Alert } from '@/components/ui/alert';
import { RetryAlert, SlowNotice } from '../components/chat-turn-notices';
import { CHAT_SLOW_NOTICE, chatErrorMessage } from '../lib/chat-errors';
import { SseSilenceError } from '../lib/sse';
import { newClientSendId, planRetry } from '../lib/turn-recovery';

const SUGGESTIONS = [
  "Éprouve query_program_items sur 'attendus CM1 maths' et dis une limite.",
  "Compare search_documents vs semantic_search sur « la loi de 2004 sur la laïcité ».",
  'Trouve les docs transversaux matérialisés en faux programmes (axe M) et propose un garde-fou.',
  'Dessine un schéma du modèle de données curriculum (render_widget).',
  'Quels nouveaux outils manquent au mode prof ? Propose-les (nom, params, RPC).',
];

let newKeySeq = 0;

const HISTORY_WINDOW = 200;

export default function AdminChat() {
  const [searchParams, setSearchParams] = useSearchParams();
  const threadId = searchParams.get('c');

  const [messages, setMessages] = useState<AdminMsg[]>([]);
  const [draft, setDraft] = useState('');
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [partialText, setPartialText] = useState('');
  const failedTurnRef = useRef<{ text: string; threadId: string | null; knownBefore: number; clientSendId: string } | null>(null);

  const activeKey = threadId ?? pendingKey;
  const live = useLiveTurn(activeKey);
  const streaming = live?.status === 'streaming';

  const scrollerRef = useRef<HTMLDivElement>(null);
  const loadedThreadRef = useRef<string | null>(null);
  const messagesRef = useRef<AdminMsg[]>([]);
  messagesRef.current = messages;

  const voice = useWebVoice({
    threadId,
    mode: 'admin',
    getHistory: () =>
      messagesRef.current
        .filter((m) => m.content && m.content.trim())
        .map((m) => ({ role: m.role === 'assistant' ? ('model' as const) : ('user' as const), text: m.content })),
    onAppend: (msgs) => setMessages((prev) => [...prev, ...msgs.map((m) => ({ role: m.role, content: m.content, widgets: m.widgets as AdminWidget[] | undefined }))]),
    /* Le fil est créé par le proxy quand la conversation est neuve : sans cette
       adoption, ce qui vient d'être dit existe en base mais reste introuvable. */
    onThread: (id) => {
      loadedThreadRef.current = id;
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('c', id); return n; }, { replace: true });
    },
    onError: setError,
  });

  useEffect(() => {
    if (!threadId) { setMessages([]); loadedThreadRef.current = null; return; }
    if (threadId === loadedThreadRef.current) return;
    let cancelled = false;
    listAdminMessages(threadId)
      .then((m) => { if (!cancelled) { setMessages(m); loadedThreadRef.current = threadId; } })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => { cancelled = true; };
  }, [threadId]);

  useEffect(() => {
    if (!live || !activeKey) return;
    if (live.status === 'done') {
      const tid = live.threadId;
      clearTurn(activeKey);
      setPendingKey(null);
      if (tid) {
        loadedThreadRef.current = tid;
        listAdminMessages(tid).then(setMessages).catch(() => {});
      }
    } else if (live.status === 'error') {
      setError(live.error === 'sse-silence-timeout' ? chatErrorMessage(new SseSilenceError()) : (live.error ?? 'Erreur'));
      setPartialText(live.text);
      if (failedTurnRef.current && live.threadId) failedTurnRef.current.threadId = live.threadId;
      clearTurn(activeKey);
      setPendingKey(null);
    }
  }, [live?.status]); /* eslint-disable-line react-hooks/exhaustive-deps */

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, live?.text, live?.tools, live?.widgets]);

  const submit = (text: string) => {
    if (!text || streaming) return;
    setDraft('');
    const clientSendId = newClientSendId();
    failedTurnRef.current = { text, threadId, knownBefore: messagesRef.current.length, clientSendId };
    setMessages((m) => [...m, { role: 'user', content: text }]);
    startStream(text, threadId, clientSendId);
  };

  const startStream = (text: string, startThread: string | null, clientSendId: string) => {
    setError(null);
    setPartialText('');
    const key = startThread ?? `new:${Date.now()}-${newKeySeq++}`;
    if (!startThread) setPendingKey(key);

    startTurn(key, text, (cb) =>
      streamAdminChat(text, startThread, {
        onDelta: cb.onDelta,
        onTool: (name, args) => cb.onTool?.(name, args),
        onWidget: (w) => cb.onWidget?.(w),
        onThread: (id, model) => {
          cb.onThread?.(id, model);
          if (id !== startThread) {
            loadedThreadRef.current = id;
            setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('c', id); return n; }, { replace: true });
          }
        },
        onDone: cb.onDone,
        onError: cb.onError,
        onSlow: () => cb.onSlow?.(),
        onActive: () => cb.onActive?.(),
      }, undefined, clientSendId),
    );
  };

  const retryTurn = async () => {
    const failed = failedTurnRef.current;
    if (!failed || streaming) return;
    setError(null);
    if (failed.threadId) {
      const tid = failed.threadId;
      const plan = await planRetry(
        async () => (await listAdminMessages(tid)).map((m) => ({ role: m.role, transcript: m.content, failed: m.failed, clientSendId: m.clientSendId })),
        failed.knownBefore,
        failed.text,
        HISTORY_WINDOW,
        failed.clientSendId,
      );
      if (plan === 'already-processed') {
        setMessages(await listAdminMessages(tid));
        setPartialText('');
        failedTurnRef.current = null;
        return;
      }
    }
    startStream(failed.text, failed.threadId, failed.clientSendId);
  };

  const send = (e: FormEvent) => { e.preventDefault(); submit(draft.trim()); };

  const newConversation = () => {
    if (streaming) return;
    setMessages([]); setError(null); setPendingKey(null);
    loadedThreadRef.current = null;
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('c'); return n; });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header floatingBurger />
      <div className="pt-[max(14px,env(safe-area-inset-top))] pr-4 pb-2 pl-[calc(72px+env(safe-area-inset-left))] border-b-[1.5px] border-border shrink-0 min-h-[56px] flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="font-extrabold text-[16px]">Labo d'outils <span className="text-[11px] text-muted-foreground">(admin)</span></h1>
          <div className="text-[12px] text-muted-foreground">Tous les outils · éprouve, diagnostique, propose · {live?.model ?? 'gemini-3.5-flash'}</div>
        </div>
        <button type="button" onClick={newConversation} disabled={streaming}
          className="shrink-0 px-3 py-2 border border-border rounded-[10px] bg-naia-blue text-card font-extrabold text-[13px] cursor-pointer font-[inherit] shadow-card disabled:cursor-not-allowed disabled:opacity-70">
          + Nouvelle
        </button>
      </div>

      <div ref={scrollerRef} className="flex-1 min-h-0 overflow-auto p-4">
        <div className="max-w-[820px] mx-auto flex flex-col gap-3">
          {messages.length === 0 && !streaming && (
            <div className="flex flex-col gap-[10px]">
              <div className="text-muted-foreground text-sm py-1">
                Banc d'essai des outils (programme structuré, recherche FTS & sémantique, introspection schéma, dessin de schéma). Objectif : pousser les limites de chaque outil et proposer de nouveaux outils.
              </div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} type="button" onClick={() => submit(s)}
                    className="text-left text-[13px] font-semibold text-foreground bg-card border border-border rounded-[12px] px-[11px] py-2 cursor-pointer font-[inherit] shadow-card max-w-[380px]">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => <MessageItem key={i} msg={m} />)}

          {streaming && live && (
            <div className="self-start max-w-[94%] w-full flex flex-col gap-[6px]">
              {live.tools.map((t, j) => <ToolChip key={j} label={adminToolLabel(t as AdminToolCall)} live={j === live.tools.length - 1 && !live.text && live.widgets.length === 0} />)}
              {live.widgets.map((w, j) => <WidgetFrame key={j} html={w.html} title={w.title ?? undefined} />)}
              {live.text ? <Bubble role="assistant" text={live.text} /> : <TypingDots />}
            </div>
          )}

          {voice.partialUser && <Bubble role="user" text={voice.partialUser} />}
          {voice.partialAssistant && <Bubble role="assistant" text={voice.partialAssistant} />}

          {streaming && live?.slow && <SlowNotice text={CHAT_SLOW_NOTICE} />}
          {!streaming && partialText && <Bubble role="assistant" text={partialText} />}
          {error && (failedTurnRef.current && !streaming
            ? <RetryAlert message={error} partial={!!partialText} onRetry={() => void retryTurn()} />
            : <Alert>{error}</Alert>)}
        </div>
      </div>

      <form onSubmit={send} className="border-t-[1.5px] border-border pt-3 pr-[max(16px,env(safe-area-inset-right))] pb-[max(12px,env(safe-area-inset-bottom))] pl-[max(16px,env(safe-area-inset-left))] bg-card shrink-0 box-border">
        <VoiceStatusBar voiceLive={voice.voiceLive} voiceState={voice.voiceState} maxWidth={820} />
        <div className="max-w-[820px] mx-auto flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Demande au labo d'éprouver un outil, diagnostiquer, dessiner…"
            disabled={streaming || voice.voiceLive}
            className="flex-1 min-w-0 px-[14px] py-[11px] border border-border rounded-[12px] font-[inherit] text-[15px] bg-secondary box-border"
          />
          <VoiceMicButton voiceLive={voice.voiceLive} streaming={streaming} onToggle={voice.toggle} />
          <button type="submit" disabled={streaming || voice.voiceLive || !draft.trim()} aria-label="Envoyer"
            className="shrink-0 w-12 p-0 bg-naia-blue border-none rounded-sm font-extrabold text-[18px] cursor-pointer font-[inherit] text-card disabled:bg-naia-info-bg disabled:text-muted-foreground disabled:cursor-not-allowed">
            {streaming ? '…' : '➤'}
          </button>
        </div>
      </form>
    </div>
  );
}

function MessageItem({ msg }: { msg: AdminMsg }) {
  if (msg.role === 'assistant' && (msg.tools?.length || msg.widgets?.length)) {
    return (
      <div className="self-start max-w-[94%] w-full flex flex-col gap-[6px]">
        {msg.tools?.map((t, j) => <ToolChip key={`t${j}`} label={adminToolLabel(t)} />)}
        {msg.widgets?.map((w, j) => <WidgetFrame key={`w${j}`} html={w.html} title={w.title ?? undefined} />)}
        <Bubble role="assistant" text={msg.content} />
      </div>
    );
  }
  return <Bubble role={msg.role} text={msg.content} />;
}

function Bubble({ role, text }: { role: 'user' | 'assistant'; text: string }) {
  const isUser = role === 'user';
  return (
    <div className={cn(
      "self-start max-w-[94%] border-none rounded-card px-[14px] py-[11px] shadow-card text-[15px] leading-[1.5] whitespace-pre-wrap break-words",
      isUser
        ? "self-end bg-naia-blue text-card"
        : "bg-naia-info-bg text-foreground"
    )}>
      {isUser ? text : <Markdown text={text} />}
    </div>
  );
}
